import React from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import Select from 'react-select/async';
import { toast } from 'react-toastify';
import { Button, Col, Form, Input, Row } from 'reactstrap';

import { LocationOn } from '@material-ui/icons';
import { AddNewContactButton } from 'components/Buttons/AddNewContactButton';
import ReactButton from 'components/Buttons/ReactButton';
import { SearchContactButton } from 'components/Buttons/SearchContactButton';
import { FlexController } from 'components/FlexControllerInput';
import { SearchTagClientInput } from 'components/Inputs/SearchTagClientInput';
import Loader from 'components/Loader';
import { ScreenPrompt } from 'components/ScreenPrompt';
import { getDefaultCountry } from 'services/ServiceCOUNTRYS';
import {
  normalizeDDI,
  normalizePhoneNumber
} from 'utils/fuctions/internationalPhoneFormatter';
import {
  formatCustomFieldsToDisplay,
  formatCustomFieldsToSubmit
} from 'views/CRM/shared/helpers/customFieldsHelpers';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import conversationService from 'views/ServicePanel/service';
import { custom_select } from '../../../../../assets/styles/multiple_select';
import { useSearchAccounts } from '../../../Account/hooks/useSearchAccounts';
import CustomFieldsInputsForm from '../../../shared/components/CustomFieldsInputsForm';
import { removeMaskSpecialCharacters } from '../../../shared/utils/phoneNumberHelpers';
import contactService from '../../services/contactService';
import AddAccountModal from '../AddAccountModal';

import { verifyRoles } from 'components/Authentication/VerifyRoles';
import ModalFilterClients from 'views/Clients/components/ModalFilterClients';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import AccountService from '../../../../../../src/views/CRM/Account/services/accountService';
import EditAccountModal from '../EditAccountModal/EditAccountModal';
import ListAccountsModal from '../ListAccountsModal/ListAccountsModal';

const ContactInputs = ({
  contact,
  recordTypeId,
  onCancelAction,
  onSaveAction,
  defaultFieldsAutoComplete = [],
  ConversaId,
  formId = 'contactForm'
}) => {
  const history = useHistory();
  const { control, handleSubmit, errors, setValue, reset, formState, watch } =
    useForm();
  const { isDirty } = formState;
  const { isSearching, filterAccounts } = useSearchAccounts();
  const [accounts, setAccounts] = useState([]);
  const [tags, setTags] = useState([]);
  const [accountResponse, setAccountResponse] = useState({});
  const [submitAttempt, setSubmitAttempt] = useState(false);

  const [isAccountSearchModalOpen, setIsAccountSearchModalOpen] =
    useState(false);
  const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false); // estado para o modal de edição
  const [modalAccounts, setModalAccounts] = useState([]);
  const [isModalAccountsOpen, setIsModalAccountsOpen] = useState(false);

  const {
    customFields: accountCustomFields,
    getCustomFields: getAccountCustomFields
  } = useEntityCustomFieldsByRecordType(entitiesName.ACCOUNT, recordTypeId);
  const {
    recordTypes: accountRecordTypes,
    getRecordTypesByUser: getAccountRecordTypesByUser
  } = useRecordTypesByUser(entitiesName.ACCOUNT);

  const { getCustomFields, customFields, isLoadingCustomFields } =
    useEntityCustomFieldsByRecordType(entitiesName.CONTACT, recordTypeId);

  useEffect(() => {
    if (contact) {
      setTags(
        contact?.tags?.map((tag) => ({
          label: tag?.name,
          value: tag?.id
        }))
      );

      if (contact.accounts && contact.accounts.length > 0) {
        const accountOptions = contact.accounts.map((account) => ({
          label: account.accountName,
          value: account.accountId,
          recordTypeId: account.recordTypeId
        }));
        setAccounts(accountOptions);
      }
      const formattedContact = formatCustomFieldsToDisplay(
        {
          ...contact,
          phone_number: contact.phone,
          phone_ddi: contact.ddi
        },
        customFields
      );

      reset(formattedContact);
    }
  }, [contact, reset, customFields]);

  useEffect(() => {
    getCustomFields();
    getAccountCustomFields();
    getAccountRecordTypesByUser();
  }, [getCustomFields, getAccountCustomFields, getAccountRecordTypesByUser]);

  const onAddedAccount = useCallback((data) => {
    setAccounts((prev) => [
      ...prev,
      {
        label: data.name,
        value: data.id,
        recordTypeId: data.record_type_id
      }
    ]);

    if (!contact) reset(defaultFieldsAutoComplete);
  }, []);

  const handleUpdateContact = async (recordTypeId, contactId, data) => {
    if (!verifyRoles(['CRM_CONTACTS_EDIT'])) {
      throw new Error('Você não tem permissão para editar contatos.');
    }

    const response = await contactService.updateContact(
      recordTypeId,
      contactId,
      data
    );

    toast.success('Contato atualizado com sucesso', {
      autoClose: 3000,
      closeOnClick: true
    });

    return response?.data?.data;
  };

  const handleCreateContact = async (recordTypeId, data) => {
    if (!verifyRoles(['CRM_CONTACTS_CREATE'])) {
      throw new Error('Você não tem permissão para criar contatos.');
    }

    const response = await contactService.createContact(recordTypeId, data);

    toast.success('Contato criado com sucesso', {
      autoClose: 3000,
      closeOnClick: true
    });

    return response?.data?.data;
  };

  const onSubmit = useCallback(
    async (data) => {
      setSubmitAttempt(true);

      data.accountIds = accounts.map((acc) => acc.value);
      data.phone = removeMaskSpecialCharacters(data.phone);

      let dataDTO = formatCustomFieldsToSubmit(data, customFields);
      dataDTO.tags = tags?.map((tag) => tag.value);

      dataDTO.phone = normalizePhoneNumber(
        data?.phone?.number || data?.__number__ || data?.phone_number
      );
      dataDTO.ddi = normalizeDDI(
        data?.phone?.ddi ||
          data?.__ddi__ ||
          data?.phone_ddi ||
          getDefaultCountry(dataDTO?.phone)?.dialCode
      );

      try {
        const savedContact = contact
          ? await handleUpdateContact(recordTypeId, contact.id, dataDTO)
          : await handleCreateContact(recordTypeId, dataDTO);

        if (contact && ConversaId) {
          await conversationService.updateClientConversation(
            ConversaId,
            contact.id
          );
        }

        if (onSaveAction && typeof onSaveAction === 'function') {
          onSaveAction(savedContact);
        } else {
          history.push('/admin/crm/contact', { recordTypeId });
        }
      } catch (error) {
        const errMsg = error?.response?.data?.message || error?.message;
        toast.error(errMsg || 'Houve um erro ao salvar o contato.', {
          autoClose: 3000,
          closeOnClick: true
        });
      }
    },
    [
      contact,
      recordTypeId,
      onSaveAction,
      history,
      tags,
      accounts,
      customFields,
      ConversaId
    ]
  );

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    if (onCancelAction && typeof onCancelAction === 'function') {
      onCancelAction();
      return;
    } else {
      history.push('/admin/crm/contact', { recordTypeId });
    }
  };

  if (isLoadingCustomFields) {
    return (
      <Row className="justify-content-md-center">
        <Loader />
      </Row>
    );
  }

  const openGoogleMaps = () => {
    window.open(
      'https://maps.google.com?q=' + contact.latitude + ',' + contact.longitude
    );
  };

  const getAccount = async (recordTypeId, id) => {
    await AccountService.getById(recordTypeId, id)
      .then((response) => {
        setAccountResponse(response?.data?.data);
      })
      .catch(() => {
        toast.error('Ocorreu um erro ao buscar informações da conta');
      });
  };

  const handleOpenModal = () => {
    setIsModalAccountsOpen(true);
    setModalAccounts(accounts);
  };

  const handleEditAccount = async (account) => {
    if (account && account?.recordTypeId && account?.value) {
      try {
        await getAccount(account.recordTypeId, account.value);
        setIsModalAccountsOpen(false);
        setIsEditAccountModalOpen(true);
      } catch (error) {
        toast.error('Ocorreu um erro ao buscar informações da conta');
      }
    } else {
      toast.error('Nenhuma conta selecionada ou recordTypeId inválido');
    }
  };

  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Form
        className="needs-validation"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleSubmit(onSubmit)(event);
        }}
        id={formId}
      >
        <Row>
          <Col className="mb-3" sm="4">
            <label className="form-control-label text-capitalize">
              Nome <span style={{ color: 'red' }}>*</span>
            </label>
            <Controller
              render={(props) => (
                <Input
                  valueController={setValue}
                  value={props.value}
                  onChange={props.onChange}
                  type="text"
                  placeholder="Nome"
                />
              )}
              control={control}
              defaultValue=""
              rules={{ required: 'Campo obrigatório' }}
              name="name"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="4">
            <FlexController
              type="phone"
              label="Telefone"
              name="phone"
              control={control}
              watch={watch}
              value={{
                phone: watch('phone'),
                ddi: watch('ddi')
              }}
              defaultValue={{
                phone: contact?.phone || '',
                ddi: contact?.ddi || getDefaultCountry()?.dialCode
              }}
              errors={errors}
              rules={{ required: 'Campo obrigatório' }}
              valueController={(fieldName, value) => {
                setValue(fieldName, value);
              }}
              validateOnSubmit={submitAttempt}
            />
          </Col>
          <Col className="mb-3" sm="4">
            <label className="form-control-label text-capitalize">Email</label>
            <Controller
              render={(props) => (
                <Input
                  name="email"
                  valueController={setValue}
                  value={props.value}
                  onChange={props.onChange}
                  placeholder="seu-email@mail.com"
                />
              )}
              control={control}
              defaultValue=""
              // O campo não é obrigatório mas é necessário validar o email com regex
              rules={{
                validate: (value) => {
                  if (!value) return true;

                  const emailRegex =
                    // eslint-disable-next-line no-useless-escape
                    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

                  return (
                    emailRegex.test(value) || 'Email com formato incorreto'
                  );
                }
              }}
              name="email"
            />
            <ErrorMessage
              errors={errors}
              name="email"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
        </Row>
        <Row>
          <Col sm="6">
            <div className="d-flex">
              <div style={{ flexGrow: '10' }} className="tour3">
                <label className="form-control-label text-capitalize">
                  Contas
                </label>
                <Select
                  styles={custom_select}
                  placeholder="Selecione contas..."
                  isMulti
                  isClearable={true}
                  name="accountIds"
                  isLoading={isSearching}
                  loadingMessage={() => 'Buscando...'}
                  noOptionsMessage={({ inputValue }) =>
                    !inputValue
                      ? 'Digite para pesquisar'
                      : 'Nenhum resultado encontrado'
                  }
                  loadOptions={async (inputValue) => {
                    const res = await filterAccounts(inputValue);
                    return (
                      res &&
                      res.map((account) => ({
                        label: account.name,
                        value: account.id,
                        recordTypeId: account?.record_type_id
                      }))
                    );
                  }}
                  onChange={(selectedOptions) => {
                    setAccounts(selectedOptions || []);
                  }}
                  value={accounts}
                  defaultValue={
                    accounts
                      ? {
                          label: accounts.label,
                          value: accounts.value
                        }
                      : ''
                  }
                  components={{
                    DropdownIndicator: () => (
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsAccountSearchModalOpen(true);
                        }}
                        style={{
                          padding: '0.6rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <SearchContactButton
                          onClick={() => setIsAccountSearchModalOpen(true)}
                          style={{ margin: 0, padding: 0 }}
                        />
                      </div>
                    )
                  }}
                />
              </div>
              <div
                style={{
                  alignSelf: 'flex-end',
                  justifyContent: 'space-between',
                  padding: '0.6rem 0.6rem',
                  marginLeft: '0.2rem'
                }}
              >
                <Button
                  size="sm"
                  color="info"
                  type="button"
                  title="Editar conta"
                  onClick={handleOpenModal}
                  disabled={!accounts}
                >
                  <li className="fas fa-user-edit"></li>
                </Button>
              </div>

              <div
                style={{
                  alignSelf: 'flex-end',
                  justifyContent: 'space-between',
                  padding: '0.6rem 0.6rem',
                  marginLeft: '-0.5rem'
                }}
              >
                <AddAccountModal
                  onAddedAccount={onAddedAccount}
                  buttonOpenModal={({ onClick }) => (
                    <AddNewContactButton onClick={onClick} />
                  )}
                />
              </div>
            </div>
          </Col>
          <Col className="mb-3" sm="6">
            <SearchTagClientInput
              setValueSelected={setTags}
              defaultValue={tags}
              isMulti={true}
              isDisabled={false}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <CustomFieldsInputsForm
            fields={customFields}
            control={control}
            errors={errors}
            setValue={setValue}
            Controller={Controller}
            watch={watch}
          />
        </Row>
        <hr />

        <ReactButton btnColor="confirmation" type="submit" form={formId}>
          Salvar
        </ReactButton>

        <ReactButton
          btnColor="cancelation"
          onClick={() => {
            return cancelAction();
          }}
        >
          Cancelar
        </ReactButton>
        {contact && contact.longitude && contact.latitude ? (
          <Button
            color="primary"
            outline
            size="sm"
            type="button"
            title="Abrir endereço do cliente no Google Maps"
            onClick={() => {
              openGoogleMaps();
            }}
            style={{ float: 'right' }}
          >
            <LocationOn />
          </Button>
        ) : null}
      </Form>
      <ModalFilterClients
        className="modal-lg"
        show={isAccountSearchModalOpen}
        onModalToggle={setIsAccountSearchModalOpen}
        setSelectedClient={(account) => {
          setAccounts((prev) => [
            ...prev,
            {
              value: account?.clientId,
              label: account?.clientName,
              recordTypeId: account?.accountRecordTypeId
            }
          ]);
          setIsAccountSearchModalOpen(false);
        }}
        account={true}
        recordTypes={accountRecordTypes}
        customFields={accountCustomFields}
      />

      <EditAccountModal
        isModalOpen={isEditAccountModalOpen}
        onModalToggle={setIsEditAccountModalOpen}
        account={accountResponse}
        recordTypeId={accountResponse?.recordTypeId}
      />

      <ListAccountsModal
        isModalAccountsOpen={isModalAccountsOpen}
        setIsModalAccountsOpen={setIsModalAccountsOpen}
        isSearching={isSearching}
        modalAccounts={modalAccounts}
        handleEditAccount={handleEditAccount}
      />
    </>
  );
};

export default ContactInputs;
